<template>
  <div class="a2p-main">
    <div class="a2p-eyebrow">{{ eyebrow }}</div>
    <h1 class="a2p-title">Authorized representative</h1>
    <p class="a2p-sub">
      The carriers verify this person against public business records — state LLC filings,
      BBB, IRS records. This is the single most common cause of rejection.
    </p>

    <A2PCallout type="warn">
      <strong>Use the business owner.</strong> Not an office manager, receptionist, or
      anyone non-ownership. Name must match the state LLC/PLLC filing. Email must be
      personal to them — shared inboxes like <code>frontdesk@</code> or <code>info@</code>
      are rejected automatically.
    </A2PCallout>

    <form @submit.prevent="handleNext" novalidate>
      <A2PField
        :num="1"
        label="Full Name — Business Owner / Principal"
        help="First and last name, matching the name on your state business registration."
        example="e.g. Jane Smith, DDS"
        :value="localData.repName"
        :validation="validateRepName(localData.repName)"
      >
        <input
          v-model="localData.repName"
          class="a2p-field-input"
          placeholder="Jane Smith, DDS"
          required
        />
      </A2PField>

      <A2PField
        :num="2"
        label="Job Title"
        pill="ownership-level only"
        :help="titleHelp"
        :value="localData.repTitle"
        :validation="validateRepTitle(localData.repTitle)"
      >
        <select v-model="localData.repTitle" class="a2p-field-select" required>
          <option value="">— Select —</option>
          <option v-for="t in REP_TITLES_ALLOWED" :key="t" :value="t">{{ t }}</option>
          <option disabled>──────────</option>
          <option
            v-for="t in REP_TITLES_REJECTED"
            :key="'rej-' + t"
            :value="t"
          >{{ t }} (will be rejected)</option>
        </select>
      </A2PField>

      <A2PField
        :num="3"
        label="Email Address"
        pill="personal, not shared"
        help="The owner's <strong>personal</strong> email — either their personal business email or a personal Gmail. <strong>Do not</strong> use front-desk@, info@, office@, admin@, or any shared inbox."
        example="e.g. jane.smith@gmail.com"
        :value="localData.repEmail"
        :validation="validateRepEmail(localData.repEmail)"
      >
        <input
          v-model="localData.repEmail"
          type="email"
          class="a2p-field-input mono"
          placeholder="jane.smith@gmail.com"
          required
        />
      </A2PField>

      <A2PField
        :num="4"
        label="Phone Number"
        help="10-digit direct phone number for the authorized representative."
        example="e.g. (555) 123-4567"
        :value="localData.repPhone"
        :validation="validatePhone(localData.repPhone)"
      >
        <input
          v-model="localData.repPhone"
          class="a2p-field-input mono"
          placeholder="(555) 123-4567"
          required
        />
      </A2PField>

      <div class="a2p-actions">
        <button type="button" class="a2p-btn a2p-btn-secondary" @click="$emit('back')">
          Back
        </button>
        <button
          type="submit"
          class="a2p-btn a2p-btn-primary"
          :disabled="!isFormValid"
        >
          Continue
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import A2PField from './components/A2PField.vue';
import A2PCallout from './components/A2PCallout.vue';
import {
  validateRepName,
  validateRepTitle,
  validateRepEmail,
  validatePhone,
  REP_TITLES_ALLOWED,
  REP_TITLES_REJECTED,
} from '@/utils/a2pValidation';

export default {
  name: 'A2PWizardRepresentative',
  components: { A2PField, A2PCallout },
  props: {
    eyebrow: String,
    repName: String,
    repTitle: String,
    repEmail: String,
    repPhone: String,
  },
  data() {
    return {
      localData: {
        repName: this.repName || '',
        repTitle: this.repTitle || '',
        repEmail: this.repEmail || '',
        repPhone: this.repPhone || '',
      },
      REP_TITLES_ALLOWED,
      REP_TITLES_REJECTED,
    };
  },
  computed: {
    titleHelp() {
      return `<strong>Must be one of:</strong> ${REP_TITLES_ALLOWED.join(', ')}.<br>` +
        `<strong>Will be rejected:</strong> ${REP_TITLES_REJECTED.join(', ')}.`;
    },
    isFormValid() {
      const nameOk = this.localData.repName.trim().length > 0 && /\s/.test(this.localData.repName.trim());
      const titleOk = REP_TITLES_ALLOWED.some(t => t.toLowerCase() === this.localData.repTitle.trim().toLowerCase());
      const email = validateRepEmail(this.localData.repEmail);
      const emailOk = email && email.level === 'ok';
      const phone = validatePhone(this.localData.repPhone);
      const phoneOk = phone && phone.level === 'ok';
      return nameOk && titleOk && emailOk && phoneOk;
    },
  },
  methods: {
    validateRepName,
    validateRepTitle,
    validateRepEmail,
    validatePhone,
    handleNext() {
      if (!this.isFormValid) return;
      this.$emit('next', { ...this.localData });
    },
  },
};
</script>

<style src="./a2p-wizard.css"></style>
